import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import { Bars3Icon, CalendarDaysIcon, PlusIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { SERVER_URL } from "../config";
import DateSearch from "../components/DateSearch";
import TableHeaderFilter from "../components/TableHeaderfilter";
import ExcelExport from "../components/ExcelExport";
import SearchableDropdown from "../components/SearchableDropdown";
import { FaGift } from 'react-icons/fa';

/* ── Status badge ─────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    present: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    absent: "bg-rose-50 text-rose-700 border border-rose-200",
    leave: "bg-amber-50 text-amber-700 border border-amber-200",
    holiday: "bg-violet-50 text-violet-700 border border-violet-200",
    sunday: "bg-sky-50 text-sky-700 border border-sky-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status] || "bg-slate-100 text-slate-600 border border-slate-200"}`}>
      {status}
    </span>
  );
};

/* ── Stat pill ────────────────────────────────────────────────────────────── */
const StatPill = ({ label, value, colorClass, dotClass }) => (
  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] sm:text-xs font-bold uppercase tracking-wider border ${colorClass}`}>
    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />
    <span>{label}</span>
    <span className="font-extrabold ml-0.5">{value}</span>
  </div>
);

/* ════════════════════════════════════════════════════════════════════════════ */
const AdminAttendanceOverview = () => {
  const [search, setSearch] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [employeeIdFilter, setEmployeeIdFilter] = useState([]);
  const [employeeNameFilter, setEmployeeNameFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [hasDateRange, setHasDateRange] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ date: "", name: "", type: "", region: "" });
  const [regionOptions, setRegionOptions] = useState([]);

  let adminEmployeeId = null;
  try {
    const employeeData = localStorage.getItem("employee");
    if (employeeData) {
      const parsed = JSON.parse(employeeData);
      adminEmployeeId = parsed.employee_id || parsed.id || null;
    }
  } catch (e) {
    console.error("Invalid employee data in localStorage");
  }

  const fetchRegions = async () => {
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/branchFetch.php`, { employee_id: adminEmployeeId });
      if (res.data.status) {
        setRegionOptions(res.data.branches.map(r => ({ label: r, value: r })));
      }
    } catch (err) { console.error(err); }
  };

  const mapAttendanceData = (data) =>
    data.map(item => ({
      ...item,
      date: item.attendance_date || item.date || new Date().toISOString().slice(0, 10),
      checkInTime: item.checkInTime ?? (item.checkin ? item.checkin : "-"),
      checkOutTime: item.checkOutTime ?? (item.checkout ? item.checkout : "-"),
      checkInLocationName: item.checkInLocationName ?? "-",
      checkOutLocationName: item.checkOutLocationName ?? "-",
    }));

  const fetchTodayAttendance = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${SERVER_URL}/DataFetch/attendanceAllToday.php`);
      setAttendanceData(res.data.status ? mapAttendanceData(res.data.attendance || []) : []);
    } catch (err) { setAttendanceData([]); }
    finally { setIsLoading(false); }
  };

  const fetchAttendanceByDateRange = async (from, to) => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/attendanceByDateRange.php`, { start_date: from, end_date: to });
      setAttendanceData(res.data.status ? mapAttendanceData(res.data.records || []) : []);
    } catch (err) { setAttendanceData([]); }
    finally { setIsLoading(false); }
  };

  const updateAttendanceStatus = async (employee_id, attendance_date, status) => {
    const adminId = JSON.parse(localStorage.getItem("employee"))?.employee_id;
    const payload = { employee_id, status, admin_id: adminId };
    if (attendance_date) payload.attendance_date = attendance_date;
    setUpdatingId(employee_id);
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/updateAttendanceStatus.php`, payload);
      if (!res.data.status) {
        alert(res.data.message);
      } else {
        setAttendanceData(prev =>
          prev.map(row =>
            row.employee_id === employee_id && row.date === (attendance_date || row.date)
              ? { ...row, status } : row
          )
        );
      }
    } catch (err) { console.error(err); }
    finally { setUpdatingId(null); }
  };

  const handleDateSearch = (from, to, hasRange) => {
    setFromDate(from || "");
    setToDate(to || "");
    setHasDateRange(!!hasRange);
    hasRange ? fetchAttendanceByDateRange(from, to) : fetchTodayAttendance();
  };

  useEffect(() => { fetchTodayAttendance(); fetchRegions(); }, []);

  const employeeIdOptions = useMemo(() => (
    [...new Set(attendanceData.map(r => r.employee_id))].map(id => ({ label: String(id), value: String(id) }))
  ), [attendanceData]);

  const employeeNameOptions = useMemo(() => (
    [...new Set(attendanceData.map(r => r.employee_name))].map(name => ({ label: name, value: name }))
  ), [attendanceData]);

  const statusOptions = useMemo(() => (
    [...new Set(attendanceData.map(r => r.status))].map(s => ({ label: s.toUpperCase(), value: s }))
  ), [attendanceData]);

  const filteredData = useMemo(() => {
    const q = search.toLowerCase();
    return attendanceData.filter(row => {
      const matchesSearch = String(row.employee_id || "").toLowerCase().includes(q) || String(row.employee_name || "").toLowerCase().includes(q);
      const matchesEmpId = employeeIdFilter.length === 0 || employeeIdFilter.includes(String(row.employee_id));
      const matchesEmpName = employeeNameFilter.length === 0 || employeeNameFilter.includes(row.employee_name);
      const matchesStatus = statusFilter.length === 0 || statusFilter.includes(row.status);
      return matchesSearch && matchesEmpId && matchesEmpName && matchesStatus;
    });
  }, [attendanceData, search, employeeIdFilter, employeeNameFilter, statusFilter]);

  const overallStats = useMemo(() => {
    const HOLIDAY_STATUSES = ["holiday", "sunday"];
    return {
      presentCount: filteredData.filter(r => r.status === "present").length,
      absentCount: filteredData.filter(r => r.status === "absent").length,
      leaveCount: filteredData.filter(r => r.status === "leave").length,
      holidayCount: filteredData.filter(r => HOLIDAY_STATUSES.includes(r.status)).length,
    };
  }, [filteredData]);

  const singleEmployeeSelected = useMemo(() => {
    const byName = employeeNameFilter.length === 1;
    const byId = employeeIdFilter.length === 1;
    if (!byName && !byId) return null;
    const empId = byId ? employeeIdFilter[0] : null;
    const empName = byName ? employeeNameFilter[0] : null;
    const rows = attendanceData.filter(r =>
      (!empId || String(r.employee_id) === empId) &&
      (!empName || r.employee_name === empName)
    );
    if (rows.length === 0) return null;
    return rows[0].employee_name || empId;
  }, [employeeIdFilter, employeeNameFilter, attendanceData]);

  const employeeStats = useMemo(() => {
    if (!hasDateRange || !singleEmployeeSelected) return null;
    const HOLIDAY_STATUSES = ["holiday", "sunday", "third_saturday"];
    const empId = employeeIdFilter.length === 1 ? employeeIdFilter[0] : null;
    const empName = employeeNameFilter.length === 1 ? employeeNameFilter[0] : null;
    const rows = attendanceData.filter(r =>
      (!empId || String(r.employee_id) === empId) &&
      (!empName || r.employee_name === empName)
    );
    const totalDays = rows.length;
    const presentDays = rows.filter(r => r.status === "present").length;
    const leaveDays = rows.filter(r => r.status === "leave").length;
    const holidayDays = rows.filter(r => HOLIDAY_STATUSES.includes(r.status)).length;
    const workingDays = totalDays - holidayDays;
    return { totalDays, presentDays, leaveDays, holidayDays, workingDays, name: singleEmployeeSelected };
  }, [hasDateRange, singleEmployeeSelected, attendanceData, employeeIdFilter, employeeNameFilter]);

  const getActionButtons = status => {
    if (["holiday", "sunday"].includes(status)) return [];
    if (status === "present") return ["absent", "leave"];
    if (status === "absent") return ["present", "leave"];
    if (status === "leave") return ["present", "absent"];
    return [];
  };

  const ACTION_STYLES = {
    present: "text-emerald-700 border border-emerald-200 hover:bg-emerald-100 bg-emerald-50",
    absent: "text-rose-700 border border-rose-200 hover:bg-rose-100 bg-rose-50",
    leave: "text-amber-700 border border-amber-200 hover:bg-amber-100 bg-amber-50",
  };

  const excelColumns = [
    { header: "Employee ID", key: "employee_id", type: "text" },
    { header: "Employee Name", key: "employee_name", type: "text" },
    { header: "Date", key: "date", type: "text" },
    { header: "Check In", key: "checkInTime", type: "text" },
    { header: "Check In Location", key: "checkInLocationName", type: "text" },
    { header: "Check Out", key: "checkOutTime", type: "text" },
    { header: "Check Out Location", key: "checkOutLocationName", type: "text" },
    { header: "Status", key: "status", type: "text" },
  ];

  const handleHolidaySubmit = async () => {
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/addHoliday.php`, holidayForm);
      if (res.data.status) {
        alert("Holiday added successfully");
        setShowHolidayModal(false);
        setHolidayForm({ date: "", name: "", type: "", region: "" });
        fetchTodayAttendance();
      } else {
        alert(res.data.message);
      }
    } catch (err) { console.error(err); }
  };

  /* ── Input style ── */
  const inputCls = "w-full bg-indigo-50/40 border border-indigo-100 rounded-xl px-3 py-2.5 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white transition-all";


  const OFFICE_START = "10:40:00";

const getRemark = (row) => {
  const HOLIDAY_STATUSES = ["holiday", "sunday"];

  // ✅ Holiday / Sunday
  if (HOLIDAY_STATUSES.includes(row.status)) {
    return row.status.toUpperCase();
  }

  // ❌ No check-in → Absent
  if (!row.checkInTime || row.checkInTime === "-") {
    return "Absent";
  }

  const toSeconds = (time) => {
    const [h, m, s] = time.split(":").map(Number);
    return h * 3600 + m * 60 + (s || 0);
  };

  const checkInSec = toSeconds(row.checkInTime);
  const officeSec = toSeconds(OFFICE_START);

  if (checkInSec <= officeSec) {
    return "On Time";
  }

  const diff = checkInSec - officeSec;

  const h = String(Math.floor(diff / 3600)).padStart(2, "0");
  const m = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
  const s = String(diff % 60).padStart(2, "0");

  return `Late by ${h}:${m}:${s}`;
};

  /* ════════════════════════════════════════════════════════════════════════ */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .page-font { font-family: 'DM Sans', sans-serif; }
        .page-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
        .page-scroll::-webkit-scrollbar-track { background: transparent; }
        .page-scroll::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 99px; }
        .top-accent { background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%); }
        .header-bg { background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%); }
        .card-bg { background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%); }
        .add-holiday-btn { background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); box-shadow: 0 3px 12px rgba(99,102,241,0.25); }
        .add-holiday-btn:hover { box-shadow: 0 5px 18px rgba(99,102,241,0.35); transform: translateY(-1px); }
        .progress-bar { background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%); }
        .holiday-modal-submit { background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); }
      `}</style>

      <div className="min-h-screen w-full page-font" style={{ background: "#f5f7ff" }}>
        <div className="fixed top-0 left-0 right-0 h-0.5 z-50 top-accent" />

        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

        {/* ── STICKY HEADER ── */}
        <header className="sticky top-0 z-20 border-b border-indigo-100/60 header-bg shadow-sm shadow-indigo-100/50">
          <div className="px-3 sm:px-5 lg:px-7 py-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-all border border-indigo-100 flex-shrink-0"
              >
                <Bars3Icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-md shadow-indigo-200 flex-shrink-0">
                  <CalendarDaysIcon className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-sm sm:text-base lg:text-lg font-bold text-slate-800 tracking-tight whitespace-nowrap">
                  Attendance Overview
                </h1>
              </div>
            </div>
            <ExcelExport
              data={filteredData}
              columns={excelColumns}
              fileName="Attendance_Report.xlsx"
              reportTitle="Attendance Report"
              reportFilters={{ fromDate, toDate }}
            />
          </div>
        </header>

        {/* ── MAIN CONTENT ── */}
        <div className="flex flex-col gap-3 sm:gap-4 p-3 sm:p-4 lg:p-5">

          {/* ── FILTER BAR ── */}
          <div className="card-bg rounded-2xl p-3 sm:p-4 border border-indigo-100/60 shadow-sm shadow-indigo-100/40">
            {/* Stats row */}
            <div className="flex flex-wrap gap-2 mb-3">
              <StatPill label="Present" value={overallStats.presentCount} colorClass="bg-emerald-50 text-emerald-700 border-emerald-200" dotClass="bg-emerald-500" />
              <StatPill label="Absent" value={overallStats.absentCount} colorClass="bg-rose-50 text-rose-700 border-rose-200" dotClass="bg-rose-500" />
              <StatPill label="On Leave" value={overallStats.leaveCount} colorClass="bg-amber-50 text-amber-700 border-amber-200" dotClass="bg-amber-500" />
              <StatPill label="Holiday" value={overallStats.holidayCount} colorClass="bg-violet-50 text-violet-700 border-violet-200" dotClass="bg-violet-500" />
            </div>

            {/* Controls row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="flex-shrink-0">
                <DateSearch onSearch={handleDateSearch} />
              </div>
              <input
                type="text"
                placeholder="Search by Emp ID or Name"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={`${inputCls} flex-1 min-w-0`}
              />
              <button
                onClick={() => setShowHolidayModal(true)}
                className="add-holiday-btn flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Holiday</span>
              </button>
            </div>
          </div>

          {/* ── EMPLOYEE-SPECIFIC STATS ── */}
          {employeeStats && (
            <div className="card-bg rounded-2xl p-4 border border-indigo-100/60 shadow-sm shadow-indigo-100/40">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="w-1 h-5 rounded-full bg-gradient-to-b from-indigo-500 to-violet-500" />
                <span className="text-sm font-bold text-slate-800">{employeeStats.name}</span>
                <span className="text-xs text-slate-400">{fromDate} — {toDate}</span>
              </div>
              <div className="rounded-xl px-4 py-3 bg-indigo-50/50 border border-indigo-100">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Attendance Rate</span>
                  <span className="text-xs font-bold text-indigo-700">
                    {employeeStats.workingDays > 0
                      ? `${Math.round((employeeStats.presentDays / employeeStats.workingDays) * 100)}%`
                      : "—"}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden bg-indigo-100">
                  <div
                    className="h-full rounded-full transition-all duration-700 progress-bar"
                    style={{
                      width: employeeStats.workingDays > 0
                        ? `${Math.round((employeeStats.presentDays / employeeStats.workingDays) * 100)}%`
                        : "0%",
                    }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-3">
                  <span className="text-[10px] text-slate-500">{employeeStats.presentDays} present</span>
                  <span className="text-[10px] text-slate-500">{employeeStats.leaveDays} leave</span>
                  <span className="text-[10px] text-slate-500">{employeeStats.workingDays - employeeStats.presentDays - employeeStats.leaveDays} absent</span>
                </div>
              </div>
            </div>
          )}

          {/* ── TABLE ── */}
          <div className="rounded-2xl overflow-hidden bg-white border border-indigo-100/60 shadow-sm shadow-indigo-100/40">
            <div className="overflow-x-auto overflow-y-auto max-h-[520px] page-scroll">
              <table className="min-w-[900px] w-full text-sm">
                <thead className="sticky top-0 z-10" style={{ background: "#f8faff" }}>
                  <tr>
                    <th className="p-3 text-left border-b border-indigo-100/60">
                      <TableHeaderFilter label="Emp ID" options={employeeIdOptions} selectedValues={employeeIdFilter} onChange={setEmployeeIdFilter} fullWidth />
                    </th>
                    <th className="p-3 text-left border-b border-indigo-100/60">
                      <TableHeaderFilter label="Name" options={employeeNameOptions} selectedValues={employeeNameFilter} onChange={setEmployeeNameFilter} fullWidth />
                    </th>
                    {["Date", "Check In", "Check In Location", "Check Out", "Check Out Location", "Remark"].map(h => (
                      <th key={h} className="p-3 text-center border-b border-indigo-100/60">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{h}</span>
                      </th>
                    ))}
                    <th className="p-3 text-center border-b border-indigo-100/60">
                      <TableHeaderFilter label="Status" options={statusOptions} selectedValues={statusFilter} onChange={setStatusFilter} fullWidth />
                    </th>
                    <th className="p-3 text-center border-b border-indigo-100/60">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={9} className="p-10 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-6 h-6 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm text-slate-400">Loading…</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredData.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="p-10 text-center text-slate-400 text-sm">No attendance data found.</td>
                    </tr>
                  ) : (
                    filteredData.map((row, i) => (
                      <tr
                        key={i}
                        className={`border-t border-slate-100 transition-colors hover:bg-indigo-50/40 ${updatingId === row.employee_id ? "opacity-40 pointer-events-none" : ""}`}
                      >
                        <td className="p-3 text-slate-500 text-xs font-medium">{row.employee_id}</td>
                        <td className="p-3 text-slate-800 text-xs font-semibold">{row.employee_name}</td>
                        <td className="p-3 text-center text-slate-500 text-xs">{row.date}</td>
                        <td className="p-3 text-center text-slate-500 text-xs font-mono">{row.checkInTime}</td>
                        <td className="p-3 text-center text-slate-400 text-xs">{row.checkInLocationName}</td>
                        <td className="p-3 text-center text-slate-500 text-xs font-mono">{row.checkOutTime}</td>
                        <td className="p-3 text-center text-slate-400 text-xs">{row.checkOutLocationName}</td>
                        <td className="p-3 text-center text-xs font-semibold">
                          {(() => {
                            const remark = getRemark(row);

                            if (remark === "On Time") {
                              return <span className="text-emerald-600">{remark}</span>;
                            }
                            if (remark === "Absent") {
                              return <span className="text-rose-500">{remark}</span>;
                            }
                            return <span className="text-amber-600">{remark}</span>;
                          })()}
                        </td>
                        <td className="p-3 text-center"><StatusBadge status={row.status} /></td>
                        <td className="p-3 text-center">
                          <div className="flex gap-1.5 justify-center flex-wrap">
                            {getActionButtons(row.status).map(opt => (
                              <button
                                key={opt}
                                onClick={() => updateAttendanceStatus(row.employee_id, row.date, opt)}
                                disabled={updatingId === row.employee_id}
                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${ACTION_STYLES[opt]}`}
                              >
                                {opt === "present" ? "✓ " : opt === "absent" ? "✕ " : ""}
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </button>
                            ))}
                            {getActionButtons(row.status).length === 0 && (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── HOLIDAY MODAL ── */}
        {showHolidayModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(8px)" }}>
            <div className="w-full max-w-md rounded-2xl bg-white border border-indigo-100 p-5 shadow-2xl shadow-indigo-100/50">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                    <FaGift className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800">Create Holiday</h2>
                    <p className="text-xs text-slate-400">Add a new holiday entry</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHolidayModal(false)}
                  className="w-8 h-8 rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center transition-all"
                >✕</button>
              </div>

              <div className="flex flex-col gap-3">
                <input type="date" value={holidayForm.date} onChange={e => setHolidayForm({ ...holidayForm, date: e.target.value })} className={inputCls} />
                <input type="text" placeholder="Holiday Name" value={holidayForm.name} onChange={e => setHolidayForm({ ...holidayForm, name: e.target.value })} className={inputCls} />
                <select value={holidayForm.type} onChange={e => setHolidayForm({ ...holidayForm, type: e.target.value })} className={inputCls} style={{ appearance: "auto" }}>
                  <option value="">Select Type</option>
                  <option value="national">National</option>
                  <option value="regional">Regional</option>
                  <option value="company">Company</option>
                </select>
                <SearchableDropdown label="Applicable Region" options={regionOptions} value={holidayForm.region} onChange={(val) => setHolidayForm({ ...holidayForm, region: val })} placeholder="Select Region" />
              </div>

              <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
                <button onClick={() => setShowHolidayModal(false)} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-all">Cancel</button>
                <button onClick={handleHolidaySubmit} className="holiday-modal-submit px-5 py-2 rounded-xl text-sm font-bold text-white transition-all">Save Holiday</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminAttendanceOverview;